import { INTAKT_CAMPAIGN_LINKS, ZASM_CAMPAIGN_LINK, ZASM_SELECTION_LINK, isNotPRO } from "@/helpers/constants";
import { getPlantIdBySubCRId } from "@/helpers/utils";
import { SubCampaignFeedback, SubCampaignInfo } from "@/types";

export const formatValue = (value) => (value < 0 ? '' : value);

// preliminaryInformation is now computed/read-only (fed by the new /feedback endpoint) and must not be sent back on save.
export const buildFeedbackSavePayload = (feedback: SubCampaignFeedback) => ({
  ...feedback,
  campaigns: feedback?.campaigns?.map((campaign) => {
    const { preliminaryInformation, ...campaignWithoutPreliminaryInformation } = campaign;

    return campaignWithoutPreliminaryInformation;
  }),
});
export const EmptySubCRCampaign = {
  campaignManagerEntry: {
    campaignNumber: '',
    campaignTitle: '',
    campaignState: '',
    campaignClassifications: [],
  },
  preliminaryInformation: {
    dynamic: null,
    validatedCars: 0,
    noRework: 0,
    reworkNotStarted: 0,
    reworkOpen: 0,
    reworkFinished: 0,
    okWithoutRework: 0,
    reworkInField: 0,
    blockingLocations: [],
  },
  internalReworkTimes: {
    shuntingAlways: -1,
    shuntingWithRework: -1,
    inspection: -1,
    rework: -1,
    diagnosis: -1,
    rollerTest: -1,
    total: -1,
  },
  externalReworkTimes: {
    inspection: -1,
    rework: -1,
    diagnosis: -1,
    rollerTest: -1,
    total: -1,
  },
  billing: {
    costCenter: '',
    pmContract: '',
    coNumber: '',
  },
  additionalInformationAboutTheCase: {
    campaignStatus: '',
  },
  zasmIntaktSynchronization: {
    updatedByZASM: false,
    lastZASMUpdatedDate: '',
    zasmId: '',
    zasmFieldsSynchronized: [],
    updatedByINTAKT: false,
    lastINTAKTUpdateDate: '',
    intaktFieldsSynchronized: [],
  },
};

const validateLenght = (value: string) => value.length <= 12;

export const fieldIsValid = (label: string, value: string) => {
  if (label === 'campaignNumber') {
    // @ts-ignore:next-line
    return (validateLenght(value) && value >= 0 && value <= 999999999999);
  }
  if (['shuntingWithRework', 'shuntingAlways', 'rollerTest', 'rework', 'inspection', 'diagnosis', 'time'].includes(label)) {
    return (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 9999) || value === '';
  }

  return parseInt(value, 10) >= 0 && parseInt(value, 10) <= 999999 || value === '';
};

export const buildExternalUrls = (ZASMId: string, subCampaignInfo: SubCampaignInfo, campaignNumber: string) => {
  const envForExternal = isNotPRO ? 'INT' : 'PRO';
  const [campaignZASMURL, selectionZASMURL, campaignINTAKTURL] = [ZASM_CAMPAIGN_LINK[envForExternal], ZASM_SELECTION_LINK[envForExternal], INTAKT_CAMPAIGN_LINKS[envForExternal]];
  const campaignINTAKTURLMounted = `${campaignINTAKTURL}?campaignnumber=${campaignNumber}&cartid=${subCampaignInfo?.id}`;
  if (!ZASMId || !subCampaignInfo) return { campaignZASMURL: '', selectionZASMURL: '', campaignINTAKTURL: campaignINTAKTURLMounted };

  const subCRLang = subCampaignInfo?.plantLanguage?.toUpperCase();
  const campaignURLMounted = `${campaignZASMURL}?id=${ZASMId}`;
  const selectionURLMounted = `${selectionZASMURL}?cartId=${subCampaignInfo?.id}&title=${encodeURIComponent(subCampaignInfo?.title?.[subCRLang])}&initialPlantId=${getPlantIdBySubCRId(subCampaignInfo?.id)}`;

  return { campaignZASMURL: campaignURLMounted, selectionZASMURL: selectionURLMounted, campaignINTAKTURL: campaignINTAKTURLMounted };
};

export const openZASMWindows = (campaignZASMURL: string = '', isSelection: boolean = false, selectionZASMURL: string = '') => {
  const [screenWidth, screenHeigth] = [window.screen.width, window.screen.height];
  const urlsNotLoaded: string[] = [];
  const campaignWindow = window.open(
    campaignZASMURL,
    'ZASM Campaign',
    `resizable,scrollbars,directories,location,toolbar,status,width=${screenWidth / 1.5},height=${screenHeigth / 1.5},top=0,left=0`,
  );
  if (popupBlockerChecker(campaignWindow)) urlsNotLoaded.push(campaignZASMURL);

  if (isSelection) {
    const selectionWindow = window.open(
      selectionZASMURL,
      'ZASM Campaign Selection',
      `resizable,scrollbars,directories,location,toolbar,status,width=${screenWidth / 1.9},height=${screenHeigth / 2},top=0,left=0`,
    );
    if (popupBlockerChecker(selectionWindow)) urlsNotLoaded.push(selectionZASMURL);
  }
  return urlsNotLoaded;
};

export const openINTAKTWindow = (campaignINTAKTURL: string) => {
  const [screenWidth, screenHeigth] = [window.screen.width, window.screen.height];
  const intaktWindow = window.open(
    campaignINTAKTURL,
    'INTAKT Campaign',
    `resizable,scrollbars,directories,location,toolbar,status,width=${screenWidth / 1.5},height=${screenHeigth / 1.5},top=0,left=0`,
  );
  if (popupBlockerChecker(intaktWindow)) return [campaignINTAKTURL];
  return [];
}

const popupBlockerChecker = (popup: Window | null | undefined) => {
  if (popup == null || typeof (popup) == 'undefined') {
    return true;
  }
  return false;
}

export const getZasmCampaignState = (campaignStateZASM: string) => ({
  enumAPI: campaignStateZASM?.toUpperCase()?.replaceAll(' ', '_'),
  EN: campaignStateZASM,
  DE: campaignStateZASM,
});

export const isValidCampaignNumberFormat = (campaignNumber: string) => {
  return /^[0-9]{12}$/.test(campaignNumber);
}

export const isZASMUrls = (urls: string[] = []) => urls.every((url) => url.includes('https://zasm'));

export const getTotalReworkTime = (fieldsValues: number[]) => fieldsValues.reduce((acc, value) => acc + (value >= 0 ? value : 0), 0);
